import clsx from 'clsx'
import React from 'react'
import { useRouter } from 'next/router'
import shallow from 'zustand/shallow'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import {
  getClassesByPhase,
  getSchoolLevel,
} from 'app/AssessmentPage/utils/class'
import { getFirstAndLastItem } from 'utils/array'
import { useSubjectAssessmentSavedFilterStore } from 'stores/subjectAssessmentSavedFilter'
import useSatuEventAssessment from 'app/AssessmentPage/utils/useSatuEventAssessment'
import { EVENT_NAME_ASSESSMENT_SUBJECT_CLICK_SEARCH_V1 } from 'app/AssessmentPage/data'

import type { TFase, TFaseGroup } from 'types/classAndPhases'

interface TSearchSectionProps {
  selectedFaseGroup: TFaseGroup
  selectedFase: TFase
  selectedMapel: string
  setIsFaseSheetOpen: (isFaseSheetOpen: boolean) => void
  setIsMapelSheetOpen: (isMapelSheetOpen: boolean) => void
}

const SearchSection = ({
  selectedFaseGroup,
  selectedFase,
  selectedMapel,
  setIsFaseSheetOpen,
  setIsMapelSheetOpen,
}: TSearchSectionProps) => {
  const { SETrackEventAssessment } = useSatuEventAssessment()
  const router = useRouter()
  const { setKeyword: setStoreKeyword, subjectAssessmentSavedFilters } =
    useSubjectAssessmentSavedFilterStore(
      ({ setKeyword, subjectAssessmentSavedFilters }) => ({
        setKeyword,
        subjectAssessmentSavedFilters,
      }),
      shallow
    )
  const [isKeywordActive, setIsKeywordActive] = React.useState<boolean>(false)
  const [keyword, setKeyword] = React.useState<string>('')

  const classes = getClassesByPhase(selectedFase)
  const phaseClasses = React.useMemo(() => {
    if (!classes) return ''
    const parsedClasses = classes?.map((klass) => klass.replace('Kelas ', ''))
    if (parsedClasses?.length > 2)
      return getFirstAndLastItem(parsedClasses as string[]).join('-')
    return parsedClasses?.join('-')
  }, [classes])

  const showFaseGroup = selectedFase && selectedFaseGroup !== 'Umum'

  const schoolLevel = React.useMemo(
    () => getSchoolLevel(selectedFase, selectedFaseGroup),
    [selectedFase, selectedFaseGroup]
  )

  const onSearchAssessmentClicked = () => {
    SETrackEventAssessment({
      eventName: EVENT_NAME_ASSESSMENT_SUBJECT_CLICK_SEARCH_V1,
      eventParams: {
        event_detail: {
          assessment_level: schoolLevel,
          assessment_phase: selectedFase,
          assessment_subject: selectedMapel,
          assessment_keyword: keyword || '',
        },
      },
    })
    router.push({
      pathname: '/assessment/assessments',
      query: {
        title: keyword,
        phase: selectedFase,
        schoolLevel,
        subject: selectedMapel,
      },
    })
  }

  React.useEffect(() => {
    const { keyword: storeKeyword } = subjectAssessmentSavedFilters

    if (storeKeyword) {
      setKeyword(storeKeyword)
      setIsKeywordActive(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <main className="w-full bg-white px-4 pb-4 min-h-[calc(100vh-54px)]">
      <Text variant="heading-lg" className="my-4">
        Pilih jenis asesmen pembelajaran yang sesuai
      </Text>
      <section className="mb-5 flex flex-col gap-y-4">
        <span
          onClick={() => setIsFaseSheetOpen(true)}
          className="flex w-full cursor-pointer justify-between rounded-[4px] !border !border-default py-3 px-4"
        >
          <Text>
            {selectedFase ? `Fase ${selectedFase}` : 'Pilih fase (kelas)'}
            {phaseClasses ? ` • Kelas ${phaseClasses}` : ''}
            {showFaseGroup && ` ${selectedFaseGroup}`}
          </Text>
          <Icon>arrow_drop_down</Icon>
        </span>

        {/* Mapel Select */}
        <span>
          <span
            onClick={() => {
              if (selectedFase && selectedFase !== 'Fondasi')
                setIsMapelSheetOpen(true)
            }}
            className={clsx(
              selectedFase
                ? '!border-default'
                : 'border-disabled bg-surface-disabled !cursor-not-allowed',
              'flex w-full cursor-pointer justify-between rounded-[4px] !border py-3 px-4'
            )}
          >
            <Text color={selectedFase ? 'default' : 'disabled'}>
              {selectedMapel || 'Pilih mata pelajaran'}
            </Text>
            <Icon>arrow_drop_down</Icon>
          </span>
        </span>
      </section>

      {!isKeywordActive && !keyword && (
        <div
          onClick={() => setIsKeywordActive(true)}
          className={clsx(
            'mb-6 flex cursor-pointer items-center gap-x-2 !pl-0 !font-semibold',
            !selectedMapel
              ? 'pointer-events-none cursor-not-allowed opacity-40'
              : ''
          )}
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-icon-informational">
            <Icon fontSize="small" color="inverse" className="bg-action ">
              add
            </Icon>
          </span>
          <Text
            color="primary"
            className="text-[15px] !font-semibold !underline"
          >
            Cari judul spesifik
          </Text>
        </div>
      )}
      {isKeywordActive && (
        <span className="group mb-4 flex h-[50px] w-full items-center justify-between rounded-[4px] border border-default px-4 py-3">
          <input
            value={keyword}
            onChange={(e) => {
              const value = e?.target?.value
              setKeyword(value)
              setStoreKeyword(value)
            }}
            placeholder="Masukkan kata kunci"
            className="group-focus:outline-blue-530 w-full focus:outline-none"
          />
          <Icon
            onClick={() => {
              setIsKeywordActive(false)
              setKeyword('')
              setStoreKeyword('')
            }}
          >
            close
          </Icon>
        </span>
      )}
      <Button
        size="lg"
        disabled={!selectedFase || !selectedMapel}
        className="w-full"
        onClick={onSearchAssessmentClicked}
      >
        Cari Asesmen
      </Button>
    </main>
  )
}

export default SearchSection
